import Backbone from "backbone";
import enqueueIn from "nx-components/utils/enqueueIn";
import Reflection from "nexia_mobile/framework/reflection";
import Validatable from "nexia_mobile/framework/validatable";
import Collection from "nexia_mobile/framework/collection";

// We want to loop over prototype properties on purpose
/* eslint-disable guard-for-in */

class Model extends Backbone.Model {
  constructor(...args) {
    super(...args);
    this._initNestedObjects();
  }

  set(...args) {
    let key = args[0];
    let val = args[1];
    let options = args[2];
    if (key === null) {
      return this;
    }
    let attrs;
    if (_.isObject(key)) {
      attrs = key;
      options = val;
    } else {
      attrs = {};
      attrs[key] = val;
    }

    if (typeof options === "undefined" || options === null) {
      options = {};
    }

    if (!options.ignoreNestedObjects) {
      for (let name in this._nestedModels) {
        let model = this._nestedModels[name];
        if (_.has(attrs, name)) {
          model.set(model.parse(attrs[name]));
          attrs[name] = model;
        }
      }

      for (let name in this._nestedCollections) {
        let collection = this._nestedCollections[name];
        if (_.has(attrs, name)) {
          let models = collection.parse(attrs[name]);
          if (models instanceof Backbone.Collection) {
            ({ models } = models);
          }

          collection.set(models);
          attrs[name] = collection;
        }
      }
    }

    return super.set(attrs, options);
  }

  parse(attrs, options) {
    this._initNestedObjects();

    if (options && options.hasOwnProperty("asyncParseDelay")) {
      return new Promise((resolve) => {
        enqueueIn(() => {
          resolve(this.onParse(attrs, options));
        }, options.asyncParseDelay);
      });
    } else {
      return this.onParse(attrs, options);
    }
  }

  onParse(attrs) {
    return attrs;
  }

  onParseError(attrs) {
    return attrs;
  }

  toJSON() {
    let attrs = super.toJSON();

    for (let key in this._nestedModels) {
      let model = this._nestedModels[key];
      if (model instanceof Backbone.Model) {
        attrs[key] = model.toJSON();
      }
    }

    for (let key in this._nestedCollections) {
      let collection = this._nestedCollections[key];
      if (collection instanceof Backbone.Collection) {
        attrs[key] = collection.toJSON();
      }
    }

    return attrs;
  }

  sync(method, model, options) {
    return super.sync(method, model, this._defaultAjaxOptions(options));
  }

  validate(attrs, options) {
    // TODO: what if a nested object attribute has a validation assigned?
    let result = this._validateAttributes(attrs, options);

    // run nested object validations.
    // this implementation might seem a little weird, but fits the
    // backbone validation scheme. The validate() call is supposed to
    // only validate against the attributes passed in, not everything
    // currently stored in the model.

    for (let key in this._nestedModels) {
      let model = this._nestedModels[key];
      let attrsToValidate;
      if (attrs[key] instanceof Backbone.Model) {
        attrsToValidate = attrs[key].attributes;
      } else {
        attrsToValidate = attrs[key];
      }

      let modelResult = model.validate(attrsToValidate, options);
      if (modelResult) {
        if (result == null) {
          result = {};
        }
        result[key] = modelResult;
      }
    }

    for (let key in this._nestedCollections) {
      if (attrs[key] instanceof Backbone.Collection) {
        for (let i = 0; i < attrs[key].models.length; i++) {
          let model = attrs[key].models[i];
          if (_.isFunction(model.validate)) {
            let modelResult = model.validate(model.attributes, options);
            if (modelResult) {
              if (result == null) {
                result = {};
              }
              result[`${key}[${i}]`] = modelResult;
            }
          }
        }
      }
    }

    this._triggerValidationEvents(result, options);
    return result;
  }

  _initNestedObjects() {
    if (this._nestedModels) {
      return;
    }

    this._nestedModels = {};
    let modelDefs = Reflection.evaluate(this.nestedModels);

    for (let name in modelDefs) {
      let properties = modelDefs[name];
      this._createNestedModel(name, properties);
    }

    this._nestedCollections = {};
    let collectionDefs = Reflection.evaluate(this.nestedCollections);
    for (let name in collectionDefs) {
      let properties = collectionDefs[name];
      this._createNestedCollection(name, properties);
    }

    this.set(this._nestedModels, { silent: true, ignoreNestedObjects: true });
    this.set(this._nestedCollections, {
      silent: true,
      ignoreNestedObjects: true,
    });
  }

  _createNestedModel(...args) {
    let name = args[0];
    let properties = args[1];
    if (!properties.model) {
      properties = { model: properties };
    }
    if (!properties.model) {
      throw new Error(`Nested model '${name}' needs a model class`);
    }

    let ModelClass = properties.model;
    let model = new ModelClass();
    this._nestedModels[name] = model;
    this._listenToNestedObjectEvents(model, properties);
  }

  _createNestedCollection(name, properties) {
    let CollectionClass = properties.collection || Collection;
    let options = {};
    if (properties.model) {
      options.model = properties.model;
    }

    let collection = new CollectionClass([], options);
    this._nestedCollections[name] = collection;
    this._listenToNestedObjectEvents(collection, properties);
  }

  _listenToNestedObjectEvents(modelOrCollection, properties) {
    if (!_.isString(properties.bubbleEvents)) {
      return;
    }

    let bubbleAll;
    let eventMap;
    if (properties.bubbleEvents === "all") {
      bubbleAll = true;
    } else {
      eventMap = {};
      _.each(
        properties.bubbleEvents.split(" "),
        (eventName) => (eventMap[eventName] = true)
      );
      properties.bubbleEvents = eventMap;
    }

    this.listenTo(modelOrCollection, "all", (eventName, ...args) => {
      if (!(bubbleAll || eventMap[eventName])) {
        return;
      }
      let triggerArgs = [eventName, ...args];
      // mark the event as being triggered by this model (not the nested object)
      triggerArgs[1] = this;
      // and re-trigger the event
      this.trigger(...triggerArgs);
    });
  }

  _defaultAjaxOptions(options) {
    let defaults = {
      wait: true,
      accepts: this.constructor.mimeType || "application/json",
      dataType: "json",
    };
    return _.extend(defaults, options);
  }

  _triggerValidationEvents(errors, options, includeBackboneEvent = false) {
    this.trigger("validated", this, errors, options);
    if (_.isEmpty(errors)) {
      this.trigger("valid", this);
    } else if (includeBackboneEvent) {
      // this includeBackboneEvent conditional is here because normally backbone will
      // trigger the invalid event for us, but not in the case of server-side errors
      // where we are 'mocking' a failed validation
      this.trigger("invalid", this, errors, {});
    }
  }
}

_.extend(Model.prototype, Validatable);

export default Model;



// WEBPACK FOOTER //
// ./src/framework/model.js